import {effect, Injectable, signal} from '@angular/core';
import {catchError, Observable, of, tap} from 'rxjs';
import {AuthService} from './auth-service';
import {CompanyMainInformation} from '../models/api/crm/CompanyMainInformation';
import {CompanyActionSummary} from '../models/api/crm/CompanyActionSummary';
import {CompanyOpportunitySummary} from '../models/api/crm/CompanyOpportunitySummary';
import {CompanyDetailView} from '../models/api/crm/CompanyDetailView';
import {GroupMemberAssignmentSummary} from '../models/api/crm/GroupMemberAssignmentSummary';
import {UserManagementService} from './user-management-service';
import {CustomerCreateInput} from '../models/api/crm/CustomerCreateInput';
import {CustomerUpdateInput} from '../models/api/crm/CustomerUpdateInput';
import {CustomerDetails} from '../models/api/crm/CustomerDetails';
import {CompanyDetails} from '../models/api/crm/CompanyDetails';
import {CompanyWebLink} from '../models/api/crm/CompanyWebLink';
import {ContractCreateInput} from '../models/api/crm/ContractCreateInput';
import {ContractUpdateInput} from '../models/api/crm/ContractUpdateInput';
import {ContractDetails} from '../models/api/crm/ContractDetails';
import {ContractExperienceFeedbackCreateInput} from '../models/api/crm/ContractExperienceFeedbackCreateInput';
import {ContractExperienceFeedbackDetails} from '../models/api/crm/ContractExperienceFeedbackDetails';
import {ContractExperienceFeedbackUpdateInput} from '../models/api/crm/ContractExperienceFeedbackUpdateInput';
import {ActionCreateInput} from '../models/api/crm/ActionCreateInput';
import {ActionUpdateInput} from '../models/api/crm/ActionUpdateInput';
import {ActionDetails} from '../models/api/crm/ActionDetails';
import {CommentCreateInput} from '../models/api/crm/CommentCreateInput';
import {CommentUpdateInput} from '../models/api/crm/CommentUpdateInput';
import {CommentDetails} from '../models/api/crm/CommentDetails';
import {CrmDocumentSummary} from '../models/api/crm/CrmDocumentSummary';
import {I18nService} from './i18n-service';
import {CompanyApiService} from './company-api-service';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private readonly _companies = signal<CompanyMainInformation[]>([]);
  readonly companies = this._companies.asReadonly();

  private readonly _companiesLoading = signal<boolean>(false);
  readonly companiesLoading = this._companiesLoading.asReadonly();

  private readonly _companyError = signal<string | null>(null);
  readonly companyError = this._companyError.asReadonly();

  private readonly _companyActionFeed = signal<CompanyActionSummary[]>([]);
  readonly companyActionFeed = this._companyActionFeed.asReadonly();

  private readonly _companyActionFeedLoading = signal<boolean>(false);
  readonly companyActionFeedLoading = this._companyActionFeedLoading.asReadonly();

  private readonly _companyActionFeedError = signal<string | null>(null);
  readonly companyActionFeedError = this._companyActionFeedError.asReadonly();

  private readonly _companyOpportunityFeed = signal<CompanyOpportunitySummary[]>([]);
  readonly companyOpportunityFeed = this._companyOpportunityFeed.asReadonly();

  private readonly _companyOpportunityFeedLoading = signal<boolean>(false);
  readonly companyOpportunityFeedLoading = this._companyOpportunityFeedLoading.asReadonly();

  private readonly _companyOpportunityFeedError = signal<string | null>(null);
  readonly companyOpportunityFeedError = this._companyOpportunityFeedError.asReadonly();

  private readonly _groupMemberAssignments = signal<GroupMemberAssignmentSummary[]>([]);
  readonly groupMemberAssignments = this._groupMemberAssignments.asReadonly();

  private readonly _groupMemberAssignmentsLoading = signal<boolean>(false);
  readonly groupMemberAssignmentsLoading = this._groupMemberAssignmentsLoading.asReadonly();

  private readonly _groupMemberAssignmentsError = signal<string | null>(null);
  readonly groupMemberAssignmentsError = this._groupMemberAssignmentsError.asReadonly();

  private readonly _selectedCompanyDetail = signal<CompanyDetailView | null>(null);
  readonly selectedCompanyDetail = this._selectedCompanyDetail.asReadonly();

  private readonly _companyDetailLoading = signal<boolean>(false);
  readonly companyDetailLoading = this._companyDetailLoading.asReadonly();

  private readonly _companyDetailError = signal<string | null>(null);
  readonly companyDetailError = this._companyDetailError.asReadonly();

  private readonly _selectedCompanyId = signal<string | null>(null);
  readonly selectedCompanyId = this._selectedCompanyId.asReadonly();

  constructor(
    private readonly companyApiService: CompanyApiService,
    private readonly authService: AuthService,
    private readonly userManagementService: UserManagementService,
    private readonly i18n: I18nService
  ) {
    effect(() => {
      if (!this.authService.shouldShowProtectedContent()) {
        this._companies.set([]);
        this._companyError.set(null);
        this._companiesLoading.set(false);
        this._companyActionFeed.set([]);
        this._companyActionFeedError.set(null);
        this._companyActionFeedLoading.set(false);
        this._companyOpportunityFeed.set([]);
        this._companyOpportunityFeedError.set(null);
        this._companyOpportunityFeedLoading.set(false);
        this._groupMemberAssignments.set([]);
        this._groupMemberAssignmentsError.set(null);
        this._groupMemberAssignmentsLoading.set(false);
        this._selectedCompanyDetail.set(null);
        this._companyDetailError.set(null);
        this._companyDetailLoading.set(false);
        this._selectedCompanyId.set(null);
        return;
      }

      if (!this.authService.isAuthenticated()) {
        return;
      }

      const selectedGroupId = this.userManagementService.selectedGroupId();
      if (!selectedGroupId) {
        this._companies.set([]);
        this._companyError.set(null);
        this._companiesLoading.set(false);
        this._companyActionFeed.set([]);
        this._companyActionFeedError.set(null);
        this._companyActionFeedLoading.set(false);
        this._companyOpportunityFeed.set([]);
        this._companyOpportunityFeedError.set(null);
        this._companyOpportunityFeedLoading.set(false);
        this._groupMemberAssignments.set([]);
        this._groupMemberAssignmentsError.set(null);
        this._groupMemberAssignmentsLoading.set(false);
        return;
      }

      this.fetchCompanies(selectedGroupId);
      this.fetchCompanyActionFeed(selectedGroupId);
      this.fetchCompanyOpportunityFeed(selectedGroupId);
      this.fetchGroupMemberAssignments(selectedGroupId);
    });

    effect(() => {
      if (!this.authService.shouldShowProtectedContent()) {
        this._selectedCompanyDetail.set(null);
        this._companyDetailError.set(null);
        this._companyDetailLoading.set(false);
        return;
      }

      if (!this.authService.isAuthenticated()) {
        return;
      }

      const selectedGroupId = this.userManagementService.selectedGroupId();
      const selectedCompanyId = this._selectedCompanyId();
      if (!selectedGroupId || !selectedCompanyId) {
        this._selectedCompanyDetail.set(null);
        this._companyDetailError.set(null);
        this._companyDetailLoading.set(false);
        return;
      }

      this.fetchCompanyDetail(selectedCompanyId, selectedGroupId);
    });
  }

  public getCompanies(): void {
    const selectedGroupId = this.userManagementService.selectedGroupId();
    if (!selectedGroupId) {
      this._companies.set([]);
      this._companyError.set(null);
      this._companiesLoading.set(false);
      return;
    }

    this.fetchCompanies(selectedGroupId);
  }

  public clearCompanyError(): void {
    this._companyError.set(null);
  }

  public loadCompanyDetail(companyId: string): void {
    this._selectedCompanyId.set(companyId);
  }

  public clearCompanyDetail(): void {
    this._selectedCompanyDetail.set(null);
    this._companyDetailError.set(null);
    this._companyDetailLoading.set(false);
    this._selectedCompanyId.set(null);
  }

  public setCompanyDetailError(message: string | null): void {
    this._companyDetailError.set(message);
  }

  selectCompany(companyId: string | null) {
    this._selectedCompanyId.set(companyId);
  }

  public refreshAgentDrivenAreas(scope: CompanyRefreshScope): void {
    const groupId = this.userManagementService.selectedGroupId();
    if (!groupId) {
      return;
    }

    if (scope.companies) {
      this.fetchCompanies(groupId);
    }
    if (scope.actionFeed) {
      this.fetchCompanyActionFeed(groupId);
    }
    if (scope.opportunityFeed) {
      this.fetchCompanyOpportunityFeed(groupId);
    }
    if (scope.groupMemberAssignments) {
      this.fetchGroupMemberAssignments(groupId);
    }
    if (scope.selectedCompanyDetail && this._selectedCompanyId()) {
      this.fetchCompanyDetail(this._selectedCompanyId()!, groupId);
    }
  }

  public createCustomer(companyId: string, payload: CustomerCreateInput): Observable<CustomerDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.createCustomer(companyId, groupId, payload).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public getCustomer(companyId: string, customerId: string): Observable<CustomerDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.getCustomer(companyId, customerId, groupId);
  }

  public deleteCompany(companyId: string): Observable<void> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.deleteCompany(companyId, groupId).pipe(
      tap(() => {
        this.fetchCompanies(groupId);
        this.fetchCompanyActionFeed(groupId);
        this.fetchCompanyOpportunityFeed(groupId);
        this.fetchGroupMemberAssignments(groupId);
        if (this._selectedCompanyId() === companyId) {
          this.clearCompanyDetail();
        }
      })
    );
  }

  public updateCustomer(companyId: string, customerId: string, payload: CustomerUpdateInput): Observable<CustomerDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.updateCustomer(companyId, customerId, groupId, payload).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public deleteCustomer(companyId: string, customerId: string): Observable<void> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.deleteCustomer(companyId, customerId, groupId).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public setMainContact(companyId: string, mainContactId: string | null): Observable<CompanyDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.setMainContact(companyId, groupId, mainContactId).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public setOwnerUser(companyId: string, ownerUserId: string | null): Observable<CompanyDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.setOwnerUser(companyId, groupId, ownerUserId).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public updateCompanyWebLinks(companyId: string, webLinks: CompanyWebLink[]): Observable<CompanyDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.updateCompanyWebLinks(companyId, groupId, {webLinks}).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public createContract(companyId: string, payload: ContractCreateInput): Observable<ContractDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.createContract(companyId, groupId, payload).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public getContract(companyId: string, contractId: string): Observable<ContractDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.getContract(companyId, contractId, groupId);
  }

  public updateContract(companyId: string, contractId: string, payload: ContractUpdateInput): Observable<ContractDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.updateContract(companyId, contractId, groupId, payload).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public deleteContract(companyId: string, contractId: string): Observable<void> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.deleteContract(companyId, contractId, groupId).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public createExperienceFeedback(
    companyId: string,
    contractId: string,
    payload: ContractExperienceFeedbackCreateInput
  ): Observable<ContractExperienceFeedbackDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.createExperienceFeedback(companyId, contractId, groupId, payload).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public getExperienceFeedback(companyId: string, contractId: string, rexId: string): Observable<ContractExperienceFeedbackDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.getExperienceFeedback(companyId, contractId, rexId, groupId);
  }

  public updateExperienceFeedback(
    companyId: string,
    contractId: string,
    rexId: string,
    payload: ContractExperienceFeedbackUpdateInput
  ): Observable<ContractExperienceFeedbackDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.updateExperienceFeedback(companyId, contractId, rexId, groupId, payload).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public deleteExperienceFeedback(companyId: string, contractId: string, rexId: string): Observable<void> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.deleteExperienceFeedback(companyId, contractId, rexId, groupId).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public createAction(companyId: string, payload: ActionCreateInput): Observable<ActionDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.createAction(companyId, groupId, payload).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public getAction(companyId: string, actionId: string): Observable<ActionDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.getAction(companyId, actionId, groupId);
  }

  public updateAction(companyId: string, actionId: string, payload: ActionUpdateInput): Observable<ActionDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.updateAction(companyId, actionId, groupId, payload).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public deleteAction(companyId: string, actionId: string): Observable<void> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.deleteAction(companyId, actionId, groupId).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public createComment(companyId: string, payload: CommentCreateInput): Observable<CommentDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.createComment(companyId, groupId, payload).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public getComment(companyId: string, commentId: string): Observable<CommentDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.getComment(companyId, commentId, groupId);
  }

  public updateComment(companyId: string, commentId: string, payload: CommentUpdateInput): Observable<CommentDetails> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.updateComment(companyId, commentId, groupId, payload).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public deleteComment(companyId: string, commentId: string): Observable<void> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.deleteComment(companyId, commentId, groupId).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public getCommentDocuments(companyId: string, commentId: string): Observable<CrmDocumentSummary[]> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.getCommentDocuments(companyId, commentId, groupId);
  }

  public uploadCommentDocument(companyId: string, commentId: string, file: File): Observable<CrmDocumentSummary> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.uploadCommentDocument(companyId, commentId, groupId, file).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public downloadCommentDocument(companyId: string, commentId: string, documentId: string): Observable<Blob> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.downloadCommentDocument(companyId, commentId, documentId, groupId);
  }

  public deleteCommentDocument(companyId: string, commentId: string, documentId: string): Observable<void> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.deleteCommentDocument(companyId, commentId, documentId, groupId).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public getCompanyDocuments(companyId: string): Observable<CrmDocumentSummary[]> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.getCompanyDocuments(companyId, groupId);
  }

  public uploadCompanyDocument(companyId: string, file: File): Observable<CrmDocumentSummary> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.uploadCompanyDocument(companyId, groupId, file).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public deleteCompanyDocument(companyId: string, documentId: string): Observable<void> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.deleteCompanyDocument(companyId, documentId, groupId).pipe(
      tap(() => this.refreshCompanyState(companyId, groupId))
    );
  }

  public downloadCompanyDocument(companyId: string, documentId: string): Observable<Blob> {
    const groupId = this.requireSelectedGroupId();
    return this.companyApiService.downloadCompanyDocument(companyId, documentId, groupId);
  }

  private fetchCompanies(groupId: string): void {
    this._companiesLoading.set(true);
    this._companyError.set(null);

    this.companyApiService.getCompanies(groupId)
      .pipe(
        catchError(() => {
          this._companiesLoading.set(false);
          this._companyError.set(this.i18n.t('errors.companies.load'));
          return of([]);
        })
      )
      .subscribe(value => {
        this._companies.set(value);
        this._companiesLoading.set(false);
      });
  }

  private fetchCompanyDetail(companyId: string, groupId: string): void {
    this._companyDetailLoading.set(true);
    this._companyDetailError.set(null);

    this.companyApiService.getCompanyDetail(companyId, groupId)
      .pipe(
        catchError(() => {
          this._selectedCompanyDetail.set(null);
          this._companyDetailLoading.set(false);
          this._companyDetailError.set(this.i18n.t('errors.companies.detail'));
          return of(null);
        })
      )
      .subscribe(value => {
        this._selectedCompanyDetail.set(value == null ? null : {
          ...value,
          contacts: value.contacts ?? [],
          contracts: value.contracts ?? [],
          experienceFeedbacks: value.experienceFeedbacks ?? [],
          actions: value.actions ?? [],
          comments: value.comments ?? [],
          documents: value.documents ?? [],
          company: {
            ...value.company,
            webLinks: value.company.webLinks ?? []
          }
        });
        this._companyDetailLoading.set(false);
      });
  }

  private fetchCompanyActionFeed(groupId: string): void {
    this._companyActionFeedLoading.set(true);
    this._companyActionFeedError.set(null);

    this.companyApiService.getCompanyActionFeed(groupId)
      .pipe(
        catchError(() => {
          this._companyActionFeed.set([]);
          this._companyActionFeedLoading.set(false);
          this._companyActionFeedError.set(this.i18n.t('errors.companies.load'));
          return of([]);
        })
      )
      .subscribe(value => {
        this._companyActionFeed.set(value ?? []);
        this._companyActionFeedLoading.set(false);
      });
  }

  private fetchCompanyOpportunityFeed(groupId: string): void {
    this._companyOpportunityFeedLoading.set(true);
    this._companyOpportunityFeedError.set(null);

    this.companyApiService.getCompanyOpportunityFeed(groupId)
      .pipe(
        catchError(() => {
          this._companyOpportunityFeed.set([]);
          this._companyOpportunityFeedLoading.set(false);
          this._companyOpportunityFeedError.set(this.i18n.t('errors.companies.load'));
          return of([]);
        })
      )
      .subscribe(value => {
        this._companyOpportunityFeed.set(value ?? []);
        this._companyOpportunityFeedLoading.set(false);
      });
  }

  private fetchGroupMemberAssignments(groupId: string): void {
    this._groupMemberAssignmentsLoading.set(true);
    this._groupMemberAssignmentsError.set(null);

    this.companyApiService.getGroupMemberAssignments(groupId)
      .pipe(
        catchError(() => {
          this._groupMemberAssignments.set([]);
          this._groupMemberAssignmentsLoading.set(false);
          this._groupMemberAssignmentsError.set(this.i18n.t('errors.companies.load'));
          return of([]);
        })
      )
      .subscribe(value => {
        this._groupMemberAssignments.set(value ?? []);
        this._groupMemberAssignmentsLoading.set(false);
      });
  }

  private refreshCompanyState(companyId: string, groupId: string): void {
    this.fetchCompanies(groupId);
    this.fetchCompanyActionFeed(groupId);
    this.fetchCompanyOpportunityFeed(groupId);
    this.fetchGroupMemberAssignments(groupId);
    if (this._selectedCompanyId() === companyId) {
      this.fetchCompanyDetail(companyId, groupId);
    }
  }

  private requireSelectedGroupId(): string {
    const groupId = this.userManagementService.selectedGroupId();
    if (!groupId) {
      throw new Error(this.i18n.t('errors.groups.selectionRequired'));
    }
    return groupId;
  }
}

export interface CompanyRefreshScope {
  companies?: boolean;
  actionFeed?: boolean;
  opportunityFeed?: boolean;
  groupMemberAssignments?: boolean;
  selectedCompanyDetail?: boolean;
}
